// @ts-nocheck
import {ProductCard} from "app/components/ProductCard";
import * as React from "react";
import {Api} from "../../../store/api";
import { useState } from "react";
import styled, { css } from "styled-components/macro";
import { useOutletContext } from "react-router-dom";
import { useFeatureFlagVariantKey } from 'posthog-js/react';

const starterMonthlyPrice = '$12';
const starterYearlyPrice = '$8';
const creatorMonthlyPrice = '$30';
const creatorYearlyPrice = '$20';


interface PricingTableProps {
  small?: boolean;
  isCheckboxSelectMode?: boolean;
  hideCurrentPlan?: boolean;
  hideFreePlan?: boolean;
  hideContractCard?: boolean;
  isLanding?: boolean;
  starterButtonText?: string;
  creatorButtonText?: string;
  onStarterButtonClick?: () => void;
  onCreatorButtonClick?: () => void;
}

export function PricingTable(props: PricingTableProps) {
    const [isRedirectingToCreatorPlan, setIsRedirectingToCreatorPlan] = useState(false);
    const [isRedirectingToStarterPlan, setIsRedirectingToStarterPlan] = useState(false);
    const [yearlyToggleValue, setYearlyToggleValue] = useState(true);

    const { 
        isCheckboxSelectMode = false, 
        small = false, 
        hideCurrentPlan = false, 
        hideFreePlan = false, 
        hideContractCard = false,
        isLanding = false,
        starterButtonText,
        creatorButtonText,
        onStarterButtonClick,
        onCreatorButtonClick
    } = props;

    // Calculate column width based on which cards are hidden
    const getNumberOfCards = () => {
        if (hideFreePlan && hideContractCard) return 2;  // When both free and contract are hidden
        if (hideFreePlan || hideContractCard) return 3;
        return 4;  // When both are shown
    };

    const numberOfCards = getNumberOfCards();

    // Get user subscription status with null check
    const outletContext = useOutletContext();
    const [userProfile, isLoaded, isSignedIn, refetchUser] = outletContext || [null, false, false, null];
    const isCreator = userProfile?.plan?.includes('creator') || false;
    const inferenceCredits = userProfile?.inference_credits || 0;
    const isStarter = userProfile?.plan?.includes('starter') || false;

    // Create selectedPlan state to store which plan is checked
    const [selectedPlan, setSelectedPlan] = useState<"creator" | "starter">("creator");
    const handleCheckboxPlanSelection = (plan: "creator" | "starter") => {
        // dont select starter if already starter
        if (plan === "starter" && isStarter) {
            return;
        }
        setSelectedPlan(plan);
    }

    const handleClickCreatorPlan = async () => {
        if (isLanding && onCreatorButtonClick) {
            onCreatorButtonClick();
            return;
        }
        
        setIsRedirectingToCreatorPlan(true)
        // Track tiktok event
        // @ts-ignore
        window.ttq.track('InitiateCheckout', {
            content_type: 'product',
            content_id: 'creator-plan',
            content_name: 'Creator Plan',
            currency: 'USD',
            price: '30.00',
            quantity: 1,
            value: '30.00',
        })
        await Api.redirectToCreatorCheckout(yearlyToggleValue)
        setIsRedirectingToCreatorPlan(false)
    }

    const handleClickStarterPlan = async () => {
        if (isLanding && onStarterButtonClick) {
            onStarterButtonClick();
            return;
        }
        
        setIsRedirectingToStarterPlan(true)
        // Track tiktok event
        // @ts-ignore
        window.ttq.track('InitiateCheckout', {
            content_type: 'product',
            content_id: 'starter-plan',
            content_name: 'Starter Plan',
            currency: 'USD',
            price: '12.00',
            quantity: 1,
            value: '12.00',
        })
        await Api.redirectToStarterCheckout(yearlyToggleValue)
        setIsRedirectingToStarterPlan(false)
    }

    const onToggleYearly = () => {
        setYearlyToggleValue(!yearlyToggleValue)
    }

    const starterPlanPrice = yearlyToggleValue
      ? starterYearlyPrice
      : starterMonthlyPrice;
    const creatorPlanPrice = yearlyToggleValue
      ? creatorYearlyPrice
      : creatorMonthlyPrice;

    // const posthogVariant = useFeatureFlagVariantKey('');
    // const isNewBulletText = posthogVariant === '';

    // To handle the "upgrade now" button
    const handleUpgradeClick = async () => {
        if (isLanding) {
            if (selectedPlan === "creator" && onCreatorButtonClick) {
                onCreatorButtonClick();
            } else if (selectedPlan === "starter" && onStarterButtonClick) {
                onStarterButtonClick();
            }
            return;
        }
        
        if (selectedPlan === "creator") {
            await handleClickCreatorPlan();
        } else {
            await handleClickStarterPlan();
        }
    };

    const isStarterPlanHidden = hideCurrentPlan && isStarter;

    // Determine button text based on context
    const getStarterButtonText = () => {
        if (isLanding) return "Start Free Trial";
        if (isLanding && starterButtonText) return starterButtonText;
        return isStarter ? "CURRENT PLAN" : "UPGRADE";
    };

    const getCreatorButtonText = () => {
        if (isLanding) return "Start Free Trial";
        if (isLanding && creatorButtonText) return creatorButtonText;
        return isCreator ? "CURRENT PLAN" : "UPGRADE";
    };

    return <PricingTableContainer>
            <StyledToggle>
                <ToggleButton
                    active={yearlyToggleValue}
                    onClick={() => setYearlyToggleValue(true)}
                >
                    <span className="toggle-label">Yearly</span>
                    <OffTag active={yearlyToggleValue}>33% OFF</OffTag>
                </ToggleButton>
                <ToggleButton
                    active={!yearlyToggleValue}
                    onClick={() => setYearlyToggleValue(false)}
                >
                    Monthly
                </ToggleButton>
            </StyledToggle>
            {/* Flex container for pricing cards */}
            <PricingCardsWrapper numberOfCards={numberOfCards}>
            {!hideFreePlan && (
              <ProductCard
                  title={"FREE"}
                  checkboxStyle={isCheckboxSelectMode}
                  description={"For free users"}
                  isCompact={small}
                  features={[
                      { text: "One-time 1,000 credits", isPositive: true },
                      { text: "Access to voice swaps, stem splits, and style samplers", isPositive: true },
                      { text: "Custom voices not included", isPositive: false },
                      { text: "Audio download unavailable", isPositive: false }
                  ]}
                  creditConversions={small ? [
                      "*Swap and Split:",
                      "- 1 credit = 1 second of audio",
                      "*Style Sampler:",
                      "- 100 credits = 1 sample (1 min)"
                  ] : [
                      "*Swap and Split: 1 credit = 1 second of audio",
                      "*Style Sampler: 100 credits = 1 sample (1 min)"
                  ]}
              />
            )}
            {!isStarterPlanHidden && (
              <ProductCard
                  title={"STARTER"}
                  checkboxStyle={isCheckboxSelectMode}
                  isChecked={selectedPlan === "starter"}
                  onCheckboxChange={() => handleCheckboxPlanSelection("starter")}
                  description={"For music creators"}
                  features={[
                      { text: "1,000 credits per day for voice swaps, stem splits and style samples", isPositive: true },
                      { text: "1 free custom voice each month", isPositive: true },
                      { text: "Use custom voice models shared with you", isPositive: true }
                  ]}
                  creditConversions={[
                      "*Swap and Split: 1 credit = 1 second of audio",
                      "*Style Sampler: 100 credits = 1 sample (1 min)"
                  ]}
                  monthlyPrice={starterMonthlyPrice}
                  priceAmount={starterPlanPrice}
                  isCompact={small}
                  priceFrequency={"/mo"}
                  submitText={getStarterButtonText()}
                  onSelect={handleClickStarterPlan}
                  isSubmitting={isRedirectingToStarterPlan}
                  isCurrent={isStarter}
                  isYearly={yearlyToggleValue}
                  isLanding={isLanding}
              />
            )}
            <ProductCard
                title={"CREATOR"}
                checkboxStyle={isCheckboxSelectMode}
                isChecked={selectedPlan === "creator"}
                onCheckboxChange={() => handleCheckboxPlanSelection("creator")}
                description={"For artists and producers"}
                features={[
                    { text: "Unlimited voice swaps, stem splits and style samples", isPositive: true },
                    { text: "3 free custom voices each month", isPositive: true },
                    { text: "Unlimited custom voices for only $10 each", isPositive: true },
                    { text: "Share custom voice models", isPositive: true },
                    { text: "Premium Support", isPositive: true }
                ]}
                monthlyPrice={creatorMonthlyPrice}
                priceAmount={creatorPlanPrice}
                priceFrequency={"/mo"}
                isCompact={small}
                submitText={getCreatorButtonText()}
                onSelect={handleClickCreatorPlan}
                isSubmitting={isRedirectingToCreatorPlan}
                isCurrent={isCreator}
                isYearly={yearlyToggleValue}
                isPopular={true}
                isLanding={isLanding}
            />
            {!hideContractCard && (
              <ProductCard
                  title={"PROFESSIONAL"}
                  checkboxStyle={isCheckboxSelectMode}
                  description={"For enterprise or professional use"}
                  isCompact={small}
                  features={[
                      { text: "API access for automation & product integration", isPositive: true },
                      { text: "No wait times – concurrent training jobs", isPositive: true },
                      { text: "Advanced training config & tuning options", isPositive: true },
                      { text: "Early access to models, voices, and features", isPositive: true },
                      { text: "Priority technical & audio support", isPositive: true },
                      { text: "Let's talk about your voice AI roadmap", isPositive: true },
                  ]}
                  showContactButton={true}
                  onContactClick={() => {
                      window.open('https://www.controlla.xyz/contact-4', '_blank');
                  }}
              />
            )}
            </PricingCardsWrapper>

            {isCheckboxSelectMode && (
                <button className="primary-button" onClick={handleUpgradeClick}>
                    {isLanding ? (selectedPlan === "creator" ? creatorButtonText : starterButtonText) || "Get Started" : "Upgrade Now"}
                </button>
            )}
    </PricingTableContainer>;
}

const PricingTableContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    padding: 0;
    margin: 0;

    @media (max-width: 600px) {
        padding: 10px;
        overflow: visible;
        max-height: 100%;
        align-items: center;
        display: flex;
        justify-content: center;
    }

  
  .primary-button {
    justify-self: flex-end;
    font-family: "Jura";
    min-height: 80px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
  }

.secondary-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    border-radius: 0.9375rem;
    border: 0px;
    font-size: 18px;
    padding: 0rem;
    height: 20px;
    margin-top: 10px;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    padding-top: 20px;
    padding-bottom: 20px;
  }

`;

const StyledToggle = styled.div`
  display: flex;
  height: 44px;
  background: transparent;
  border: 1px solid #645270;
  border-radius: 17px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  max-width: 300px;
  margin: 20px 0;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ active }) =>
    active ? 'rgba(162, 89, 230, 0.18)' : 'transparent'};
  color: ${({ active }) => (active ? '#FF74E9' : '#645270')};
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: ${({ active }) => (active ? 700 : 400)};
  border: none;
  outline: none;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, font-weight 0.2s;
  border-right: 1px solid #645270;
  padding: 0 20px;

  &:last-child {
    border-right: none;
  }
  .toggle-label {
    margin-right: 8px;
  }
`;

const OffTag = styled.span<{ active: boolean }>`
  font-family: 'Jura';
  font-size: 11px;
  font-weight: 700;
  border-radius: 17px;
  border: 1px solid rgba(156, 39, 176, 0.5);
  color: ${({ active }) => (active ? '#FF74E9' : '#645270')};
  transition: background 0.2s, color 0.2s, border 0.2s;
  padding: 0 5px;
  width: 61px;
`;

// Add styled-components for the flex layout
const PricingCardsWrapper = styled.div<{ numberOfCards: number }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  gap: 0;
  margin-bottom: 32px;

  ${({ numberOfCards }) =>
    numberOfCards > 3 &&
    css`
      flex-wrap: wrap;
      gap: 12px;
    `}

  @media (max-width: 1200px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;
